import { Component, type ReactNode, type ErrorInfo } from "react";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to reporting service in production
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="bg-background flex min-h-screen items-center justify-center p-4">
          <div
            className="max-w-md text-center"
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <AlertCircle className="text-destructive mx-auto h-16 w-16" aria-hidden="true" />
            <h1 className="mt-6 text-2xl font-bold">Something went wrong</h1>
            <p className="text-muted-foreground mt-2">
              We apologize for the inconvenience. An unexpected error has occurred.
            </p>
            {this.state.error && (
              <pre className="bg-muted mt-4 overflow-auto rounded-md p-4 text-left text-xs">
                {this.state.error.message}
              </pre>
            )}
            <div className="mt-6 flex justify-center gap-4">
              <Button variant="outline" onClick={this.handleGoHome}>
                <Home className="mr-2 h-4 w-4" aria-hidden="true" />
                Go Home
              </Button>
              <Button onClick={this.handleRetry}>
                <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
                Try Again
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
